import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Drawer,
  Modal,
  Select,
  Switch,
  message,
} from '@surecompanies/core_components'
// Tag-style multi-email input: the shared Select doesn't reliably forward
// `mode="tags"`, so we use antd's Select directly here — same pattern as
// the finance module's email-list inputs (GlobalCCEmailsCard, etc.).
import { Select as AntSelect } from 'antd'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getCarriers,
  upsertCarrier,
  CarrierGroup,
  type CarrierResponseDto,
} from '../api/fetch-carrier-api'
import {
  crawlScopeToScanMode,
  getInvalidNotificationEmails,
  SCAN_MODE_TO_CRAWL_SCOPE,
  type ScanMode,
} from './carrier-upsert.schema'
import styles from '../PlanManagementV3.module.scss'

interface Props {
  open: boolean
  onClose: () => void
  onLive?: (groupCode: string) => void
}

const SCAN_OPTIONS: { mode: ScanMode; title: string; description: string }[] = [
  {
    mode: 'test',
    title: 'Test scan',
    description: 'First 3 zips per state, manual runs only',
  },
  {
    mode: 'quick',
    title: 'Quick scan',
    description: 'Every county, 1 zip per county',
  },
  {
    mode: 'full',
    title: 'Full scan',
    description: 'All zips',
  },
]

export default function CarrierConfigDrawer({ open, onClose, onLive }: Props) {
  const queryClient = useQueryClient()
  const [groupCode, setGroupCode] = useState<string | undefined>(undefined)
  const [autoSchedule, setAutoSchedule] = useState(false)
  const [scanMode, setScanMode] = useState<ScanMode>('quick')
  const [source, setSource] = useState<string | undefined>(undefined)
  const [notificationEmails, setNotificationEmails] = useState<string[]>([])
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const {
    data: carriers,
    isLoading: isLoadingCarriers,
    error: carriersError,
  } = useQuery({
    queryKey: ['planBotCarriers', 'active'],
    queryFn: () => getCarriers({ isActive: true }),
    enabled: open,
  })

  const carrierOptions = useMemo(() => {
    const list = Array.isArray(carriers) ? carriers : []
    return Array.from(
      new Map(
        list.map((c) => [
          c.groupCode,
          {
            value: c.groupCode,
            label: c.carrierName ?? c.carrierFullName ?? c.groupCode,
            carrier: c,
          },
        ])
      ).values()
    )
  }, [carriers])

  const selectedCarrier: CarrierResponseDto | undefined = useMemo(
    () => carrierOptions.find((o) => o.value === groupCode)?.carrier,
    [carrierOptions, groupCode]
  )

  const sourceOptions = selectedCarrier?.sources ?? []
  const hasSourceChoice = sourceOptions.length > 1

  useEffect(() => {
    if (carriersError) {
      console.error('Could not load carriers', carriersError)
      message.error('Failed to load carriers.')
    }
  }, [carriersError])

  // Populate form fields when a carrier is selected
  useEffect(() => {
    if (!selectedCarrier) return
    setAutoSchedule(selectedCarrier.autoSchedule)
    setScanMode(crawlScopeToScanMode(selectedCarrier.crawlScope))
    setSource(selectedCarrier.source ?? undefined)
    setNotificationEmails(selectedCarrier.notificationEmails ?? [])
    setEmailError(null)
  }, [selectedCarrier])

  // Reset state when the drawer closes
  useEffect(() => {
    if (!open) {
      setGroupCode(undefined)
      setAutoSchedule(false)
      setScanMode('quick')
      setSource(undefined)
      setNotificationEmails([])
      setEmailError(null)
    }
  }, [open])

  const doSave = useCallback(async () => {
    if (!selectedCarrier) return
    setIsSaving(true)
    try {
      await upsertCarrier({
        year: selectedCarrier.year,
        groupCode: selectedCarrier.groupCode as CarrierGroup,
        baseUrl: selectedCarrier.baseUrl,
        autoSchedule,
        isActive: selectedCarrier.isActive,
        crawlScope: SCAN_MODE_TO_CRAWL_SCOPE[scanMode],
        notificationEmails,
        ...(hasSourceChoice && source ? { source } : {}),
      })
      message.success('Carrier settings saved.')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['planBotCarriers'] }),
        queryClient.invalidateQueries({ queryKey: ['carrierTasks'] }),
      ])
      onClose()
    } catch (err) {
      console.error('Failed to save carrier config', err)
      message.error('Could not save settings. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }, [
    selectedCarrier,
    autoSchedule,
    scanMode,
    notificationEmails,
    hasSourceChoice,
    source,
    queryClient,
    onClose,
  ])

  const handleSave = useCallback(() => {
    if (!selectedCarrier) return
    const invalidEmails = getInvalidNotificationEmails(notificationEmails)
    if (invalidEmails.length > 0) {
      setEmailError(
        `Invalid email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.join(', ')}`
      )
      return
    }
    setEmailError(null)
    const carrierLabel =
      selectedCarrier.carrierName ??
      selectedCarrier.carrierFullName ??
      selectedCarrier.groupCode
    Modal.confirm({
      title: 'Save carrier settings?',
      content: `Update settings for ${carrierLabel}?`,
      okText: 'Save',
      cancelText: 'Cancel',
      onOk: () => doSave(),
    })
  }, [selectedCarrier, notificationEmails, doSave])

  return (
    <Drawer
      title="Carrier Settings"
      placement="right"
      width={400}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { overflowY: 'auto' } }}
    >
      <div className={styles.carrierConfigSection}>
        {/* Carrier select */}
        <div className={styles.carrierConfigField}>
          <span className={styles.carrierConfigLabel}>Carrier</span>
          <Select
            aria-label="Carrier"
            style={{ width: '100%' }}
            placeholder="Select a carrier"
            showSearch
            optionFilterProp="label"
            loading={isLoadingCarriers}
            value={groupCode}
            onChange={(v) => setGroupCode(v as string)}
            options={carrierOptions}
          />
        </div>

        <div className={styles.carrierConfigDivider} />

        {/* Auto Schedule */}
        <div className={styles.carrierConfigSwitchRow}>
          <div className={styles.carrierConfigSwitchLabel}>
            <span className={styles.carrierConfigSwitchTitle}>
              Auto Schedule
            </span>
            <span className={styles.carrierConfigSwitchDescription}>
              Automatically schedule fetch tasks
            </span>
          </div>
          <Switch
            checked={autoSchedule}
            onChange={setAutoSchedule}
            disabled={!selectedCarrier}
          />
        </div>

        <div className={styles.carrierConfigDivider} />

        {/* Fetch Coverage */}
        <div className={styles.carrierConfigField}>
          <span className={styles.carrierConfigLabel}>Fetch Coverage</span>
          <Select<ScanMode>
            aria-label="Fetch Coverage"
            style={{ width: '100%' }}
            disabled={!selectedCarrier}
            value={scanMode}
            onChange={setScanMode}
            options={SCAN_OPTIONS.map(({ mode, title }) => ({
              value: mode,
              label: title,
            }))}
          />
          <span className={styles.carrierConfigScanOptionDescription}>
            {SCAN_OPTIONS.find((o) => o.mode === scanMode)?.description}
          </span>
        </div>

        <div className={styles.carrierConfigDivider} />

        {/* Source — only for carriers that can be fetched from several sites */}
        {hasSourceChoice && (
          <>
            <div className={styles.carrierConfigField}>
              <span className={styles.carrierConfigLabel}>Source</span>
              <Select<string>
                aria-label="Source"
                style={{ width: '100%' }}
                value={source}
                onChange={setSource}
                options={sourceOptions.map(({ id, label }) => ({
                  value: id,
                  label,
                }))}
              />
            </div>

            <div className={styles.carrierConfigDivider} />
          </>
        )}

        {/* Notification Emails */}
        <div className={styles.carrierConfigField}>
          <span className={styles.carrierConfigLabel}>Notification Emails</span>
          <AntSelect
            aria-label="Notification Emails"
            mode="tags"
            tokenSeparators={[',', ' ', ';']}
            placeholder="ops@sureco.com"
            open={false}
            disabled={!selectedCarrier}
            style={{ width: '100%' }}
            value={notificationEmails}
            onChange={(emails: string[]) => {
              setNotificationEmails(emails)
              setEmailError(null)
            }}
          />
          {emailError ? (
            <span className={styles.carrierConfigEmailError}>{emailError}</span>
          ) : (
            <span className={styles.carrierConfigEmailHint}>
              Press Enter, comma, or semicolon after each address. Notified when
              a fetch task completes.
            </span>
          )}
        </div>

        <div className={styles.carrierConfigFooter}>
          {onLive && (
            <Button
              disabled={!selectedCarrier || isSaving}
              onClick={() =>
                selectedCarrier && onLive(selectedCarrier.groupCode)
              }
            >
              Live check
            </Button>
          )}
          <div className={styles.carrierConfigFooterActions}>
            <Button onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => void handleSave()}
              loading={isSaving}
              disabled={!selectedCarrier}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  )
}
